import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import ProductCard from '../components/product/ProductCard';
import ImageGallery from '../components/product/ImageGallery';
import ReviewList from '../components/review/ReviewList';
import ReviewForm from '../components/review/ReviewForm';
import StarRating from '../components/review/StarRating';
import { getImageUrl } from '../utils/imageUrl';
import { formatDescription } from '../utils/formatDescription';
import { expandProductCards } from '../utils/productCards';
import { Product, ProductImage, ProductVariant, Review } from '../types';
import { productAPI, reviewAPI } from '../services/api';
import { trackFacebookEvent } from '../utils/facebookPixel';
import { gtmEvents } from '../utils/gtmDataLayer';

import './ProductDetails.css';

const ProductDetails: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { addItem: addToWishlist, removeItem: removeFromWishlist, isInWishlist } = useWishlist();
  const [product, setProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);

  // Selected attribute options
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedWeight, setSelectedWeight] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'description' | 'shipping' | 'reviews'>('description');
  const [colorHexMap, setColorHexMap] = useState<Record<string, string>>({});

  useEffect(() => {
    productAPI.getColors?.().then((res: any) => {
      const list = Array.isArray(res) ? res : (res?.results || res?.data || []);
      const map: Record<string, string> = {};
      list.forEach((c: any) => {
        if (c.name && c.hex_code) map[c.name.toLowerCase()] = c.hex_code;
      });
      setColorHexMap(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true);
        const data = await productAPI.getProductBySlug(slug!);
        setProduct(data);
      } catch (error) {
        console.error('Error loading product:', error);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadProduct();
    }
  }, [slug]);

  const loadReviews = async (productId: number) => {
    try {
      setReviewsLoading(true);
      const data = await reviewAPI.getProductReviews(productId);
      const reviewsArray = Array.isArray(data) ? data : ((data as any)?.results || (data as any)?.data || []);
      setReviews(reviewsArray);
    } catch (error) {
      console.error('Error loading reviews:', error);
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  useEffect(() => {
    if (product) {
      loadReviews(product.id);
    }
  }, [product]);

  // Reset attribute selections when product changes
  useEffect(() => {
    setSelectedColor('');
    setSelectedSize('');
    setSelectedWeight('');
    setQuantity(1);
  }, [product?.id]);

  useEffect(() => {
    if (!product?.variants?.length) return;
    const colors = Array.from(new Set(product.variants.map(v => v.color).filter(Boolean))) as string[];
    const requestedColor = searchParams.get('color') || '';
    const firstColor = colors.find(c => c.toLowerCase() === requestedColor.toLowerCase()) || colors[0] || '';
    const firstSize = product.variants.find(v => !firstColor || v.color === firstColor)?.size || '';
    setSelectedColor(firstColor);
    setSelectedSize(firstSize);
  }, [product?.id, product?.variants, searchParams]);

  const currentLang = i18n.language;

  useEffect(() => {
    if (product) {
      const productName = currentLang === 'bn' ? product.name_bn : product.name_en;
      const categoryName = typeof product.category === 'object' && product.category !== null
        ? (currentLang === 'bn' ? product.category.name_bn : product.category.name_en)
        : product.category_name || '';

      // Track Facebook ViewContent
      trackFacebookEvent('ViewContent', {
        content_name: productName,
        content_ids: [product.id.toString()],
        content_type: 'product',
        value: product.final_price,
        currency: 'BDT',
        content_category: categoryName
      });

      // Track GTM/GA4 view_item
      gtmEvents.viewItem({
        id: product.id,
        name: productName,
        price: product.final_price,
        category: categoryName,
      });
    }
  }, [product, currentLang]);

  const runAddToCartAnimation = (sourceElement: HTMLElement) => {
    if (!product || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const targetElement = Array.from(
      document.querySelectorAll<HTMLElement>(
        '.mobile-bottom-navigation .mobile-cart-btn, .header-user-actions .cart-btn-top'
      )
    ).find((element) => {
      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && window.getComputedStyle(element).display !== 'none';
    });
    if (!targetElement) return;

    const sourceRect = sourceElement.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();
    const startX = sourceRect.left + sourceRect.width / 2;
    const startY = sourceRect.top + sourceRect.height / 2;
    const endX = targetRect.left + targetRect.width / 2;
    const endY = targetRect.top + targetRect.height / 2;

    const flyer = document.createElement('div');
    flyer.className = 'fly-to-cart-animation';

    const imageUrl = getImageUrl(
      product.image ||
      product.primary_image ||
      product.images?.find(img => img.is_primary)?.image ||
      product.images?.[0]?.image
    );
    if (imageUrl) {
      flyer.style.backgroundImage = `url(${imageUrl})`;
    } else {
      flyer.classList.add('without-image');
    }

    flyer.style.left = `${startX - 26}px`;
    flyer.style.top = `${startY - 26}px`;
    flyer.style.setProperty('--fly-x', `${endX - startX}px`);
    flyer.style.setProperty('--fly-y', `${endY - startY}px`);
    document.body.appendChild(flyer);
    flyer.classList.add('is-flying');

    window.setTimeout(() => {
      targetElement.classList.add('cart-target-bump');
    }, 820);

    window.setTimeout(() => {
      flyer.remove();
      targetElement.classList.remove('cart-target-bump');
    }, 1180);
  };

  const variantOptions = product?.variants || [];
  const colorOptions = Array.from(new Set(variantOptions.map(v => v.color).filter(Boolean))) as string[];
  const sizeOptions = Array.from(new Set(variantOptions.filter(v => !selectedColor || v.color === selectedColor).map(v => v.size).filter(Boolean))) as string[];

  const selectedVariant = variantOptions.find(v =>
    (!selectedColor || v.color === selectedColor) &&
    (!selectedSize || v.size === selectedSize)
  );

  const currentAvailableStock = selectedVariant
    ? (selectedVariant.stock ?? 0)
    : (product?.stock ?? 0);

  const exactVariantAvailable = selectedVariant
    ? (selectedVariant.stock ?? 0) > 0
    : Boolean(product?.in_stock);

  const handleAddToCart = (event?: React.MouseEvent<HTMLElement>) => {
    if (product) {
      if (event?.currentTarget) {
        runAddToCartAnimation(event.currentTarget);
      }

      if ((colorOptions.length || sizeOptions.length) && !selectedVariant) {
        Swal.fire({
          icon: 'warning',
          title: currentLang === 'bn' ? 'ভ্যারিয়েন্ট নির্বাচন করুন' : 'Select a valid variant',
          timer: 1800,
          showConfirmButton: false
        });
        return;
      }

      if (!exactVariantAvailable) {
        Swal.fire({
          icon: 'error',
          title: currentLang === 'bn' ? 'স্টক শেষ' : 'Out of Stock',
          text: currentLang === 'bn' ? 'এই ভ্যারিয়েন্টটি বর্তমানে স্টকে নেই।' : 'This variant is currently out of stock.',
          timer: 2000,
          showConfirmButton: false
        });
        return;
      }

      const websitePrice = Number(selectedVariant?.website_price ?? product.final_price);
      const regularPrice = Number(selectedVariant?.regular_price ?? product.price);
      const pricedProduct = {
        ...product,
        price: regularPrice,
        final_price: websitePrice,
        discount_price: websitePrice < regularPrice ? websitePrice : undefined
      };

      addItem(pricedProduct, quantity, {
        color: selectedColor,
        size: selectedSize,
        weight: selectedWeight,
        variant_id: selectedVariant?.id,
        variant_sku: selectedVariant?.sku,
      });

      trackFacebookEvent('AddToCart', {
        content_name: currentLang === 'bn' ? product.name_bn : product.name_en,
        content_ids: [product.id.toString()],
        content_type: 'product',
        value: websitePrice * quantity,
        currency: 'BDT'
      });
    }
  };

  const handleDirectOrder = () => {
    if (product) {
      if ((colorOptions.length || sizeOptions.length) && !selectedVariant) {
        Swal.fire({
          icon: 'warning',
          title: currentLang === 'bn' ? 'ভ্যারিয়েন্ট নির্বাচন করুন' : 'Select a valid variant',
          timer: 1800,
          showConfirmButton: false
        });
        return;
      }

      if (!exactVariantAvailable) {
        Swal.fire({
          icon: 'error',
          title: currentLang === 'bn' ? 'স্টক শেষ' : 'Out of Stock',
          text: currentLang === 'bn' ? 'এই ভ্যারিয়েন্টটি বর্তমানে স্টকে নেই।' : 'This variant is currently out of stock.',
          timer: 2000,
          showConfirmButton: false
        });
        return;
      }

      const websitePrice = Number(selectedVariant?.website_price ?? product.final_price);
      const regularPrice = Number(selectedVariant?.regular_price ?? product.price);
      const pricedProduct = {
        ...product,
        price: regularPrice,
        final_price: websitePrice,
        discount_price: websitePrice < regularPrice ? websitePrice : undefined
      };

      trackFacebookEvent('AddToCart', {
        content_name: currentLang === 'bn' ? product.name_bn : product.name_en,
        content_ids: [product.id.toString()],
        content_type: 'product',
        value: websitePrice * quantity,
        currency: 'BDT'
      });

      const orderData = {
        product: pricedProduct,
        quantity: quantity,
        selected_color: selectedColor,
        selected_size: selectedSize,
        selected_weight: selectedWeight,
        selected_variant_id: selectedVariant?.id,
        selected_variant_sku: selectedVariant?.sku,
      };
      sessionStorage.setItem('directOrder', JSON.stringify(orderData));
      navigate('/checkout');
    }
  };

  const handleWishlistToggle = () => {
    if (product) {
      const inWishlist = isInWishlist(product.id);
      if (inWishlist) {
        removeFromWishlist(product.id);
        Swal.fire({
          position: 'top-end',
          icon: 'info',
          title: i18n.language === 'bn' ? 'অপসারিত' : 'Removed',
          text: t('common.removed') || 'Removed from Wishlist',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
          toast: true,
        });
      } else {
        addToWishlist(product);

        trackFacebookEvent('AddToWishlist', {
          content_name: currentLang === 'bn' ? product.name_bn : product.name_en,
          content_ids: [product.id.toString()],
          content_type: 'product',
          value: product.final_price,
          currency: 'BDT'
        });

        Swal.fire({
          position: 'top-end',
          icon: 'success',
          title: i18n.language === 'bn' ? 'যোগ হয়েছে' : 'Added',
          text: t('common.added') || 'Added to Wishlist',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
          toast: true,
        });
      }
    }
  };

  const handleQuantityChange = (delta: number) => {
    const maxAllowed = Math.max(1, currentAvailableStock || 1);
    setQuantity(prev => Math.max(1, Math.min(maxAllowed, prev + delta)));
  };

  const copyCouponCode = (code: string) => {
    navigator.clipboard.writeText(code);
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: currentLang === 'bn' ? `কুপন কোড ${code} কপি করা হয়েছে!` : `Coupon ${code} copied!`,
      showConfirmButton: false,
      timer: 2000,
    });
  };

  const isHtmlEmpty = (html: string | undefined | null): boolean => {
    if (!html || html.trim() === '') return true;
    const text = html.replace(/<[^>]*>/g, '').trim();
    return text === '';
  };

  if (loading) {
    return <div className="loading-container"><p>{t('common.loading')}</p></div>;
  }

  if (!product) {
    return <div className="error-container"><p>{t('common.error')}</p></div>;
  }

  const productName = currentLang === 'bn' ? product.name_bn : product.name_en;
  const productDesc = currentLang === 'bn' ? product.description_bn : product.description_en;
  const productSummary = currentLang === 'bn' ? product.summary_bn : product.summary_en;
  const hasSummaryContent = !isHtmlEmpty(productSummary);

  const categoryObj = typeof product.category === 'object' && product.category !== null ? product.category : null;
  const categoryName = categoryObj
    ? (currentLang === 'bn' ? categoryObj.name_bn : categoryObj.name_en)
    : product.category_name || '';

// Colour-aware gallery: only this colour's own media is ever shown.
// `color_media[].gallery_images` is the authoritative per-colour source; the
// product-level `images` list references the same rows, so we deduplicate.
const colorMediaImages: ProductImage[] = (product.color_media || []).flatMap(cm =>
  (cm.gallery_images || [])
    .filter(g => g.image_url || g.image)
    .map(g => ({
      id: g.id,
      image: getImageUrl(g.image_url || g.image || ''),
      is_primary: !!g.is_main_for_color,
      display_order: g.display_order || 0,
      color: cm.color,
    }))
);

const seenImages = new Set<string>(colorMediaImages.map(i => i.image));
const legacyImages: ProductImage[] = (product.images || []).reduce<ProductImage[]>((acc, img) => {
  const url = getImageUrl(img.image);
  if (!seenImages.has(url)) {
    seenImages.add(url);
    acc.push({ ...img, image: url, color: img.color || '' });
  }
  return acc;
}, []);

const allImages: ProductImage[] = [...colorMediaImages, ...legacyImages];

// Strict per-colour gallery: never mix images from different colours.
const imagesForColor = (color: string): ProductImage[] => {
  const key = color.toLowerCase();
  const byColor = allImages
    .filter(img => img.color && img.color.toLowerCase() === key)
    .sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0));
  if (byColor.length) return byColor;

  const colorMedia = (product.color_media || []).find(cm => cm.color.toLowerCase() === key);
  if (colorMedia && colorMedia.main_image) {
    return [{ id: -1, image: getImageUrl(colorMedia.main_image), is_primary: true, display_order: 0, color }];
  }

  const variantImage = product.variants?.find(v => v.color && v.color.toLowerCase() === key)?.image;
  if (variantImage) {
    return [{ id: -2, image: getImageUrl(variantImage), is_primary: true, display_order: 0, color }];
  }
  return [];
};

const galleryImages = selectedColor ? imagesForColor(selectedColor) : allImages;
  const websitePrice = Number(selectedVariant?.website_price ?? product.final_price);
  const websiteRegularPrice = Number(selectedVariant?.regular_price ?? product.price);
  const websiteDiscountPercentage = websiteRegularPrice > websitePrice
    ? Math.round(((websiteRegularPrice - websitePrice) * 100) / websiteRegularPrice)
    : 0;

  const preferredImage = galleryImages[0]?.image || null;
  const activeSku = selectedVariant?.sku || product.sku;

  return (
    <div className="product-details-page">
      <div className="container">
        {/* Enhanced Breadcrumbs */}
        <div className="breadcrumb">
          <Link to="/">{t('nav.home')}</Link>
          <span>/</span>
          <Link to="/products">{t('nav.products')}</Link>
          {categoryName && (
            <>
              <span>/</span>
              <span className="breadcrumb-category">{categoryName}</span>
            </>
          )}
          <span>/</span>
          <span className="current">{productName}</span>
        </div>

        <div className="product-details-layout">
          {/* Gallery */}
          <div className="product-gallery">
            <ImageGallery 
              images={galleryImages} 
              productName={productName} 
              preferredImage={preferredImage} 
              youtubeEmbedUrl={product.youtube_embed_url} 
            />
          </div>

          {/* Product Meta & Purchase Info */}
          <div className="product-info">
            {/* Category, Brand & Tags */}
            <div className="product-badges-row">
              {categoryName && (
                <span className="product-category-tag">{categoryName.toUpperCase()}</span>
              )}
              {product.brand_name && (
                <span className="product-brand-tag">🏷️ {product.brand_name}</span>
              )}
              {product.is_featured && (
                <span className="product-featured-tag">★ {currentLang === 'bn' ? 'ফিচার্ড প্রোডাক্ট' : 'Featured'}</span>
              )}
            </div>

            <div className="product-header">
              <h1 className="product-title">{productName}</h1>
              <button
                className={`wishlist-btn ${isInWishlist(product.id) ? 'active' : ''}`}
                onClick={handleWishlistToggle}
                aria-label="Add to Wishlist"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </button>
            </div>

            {/* Price & Rating Display Row */}
            <div className="product-pricing-row" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px', marginTop: '4px' }}>
              <div className="price-inline-row" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                {websitePrice < websiteRegularPrice ? (
                  <>
                    <span className="current-price" style={{ fontSize: '24px', fontWeight: 800, color: 'var(--primary, #7A0C0C)' }}>৳{websitePrice}</span>
                    <span className="original-price" style={{ fontSize: '15px', color: '#9ca3af', textDecoration: 'line-through', fontWeight: 500 }}>৳{websiteRegularPrice}</span>
                    <span className="discount-badge" style={{ background: '#F5F3FF', color: '#7c3aed', fontSize: '12px', padding: '4px 8px', borderRadius: '9999px', fontWeight: 700 }}>{websiteDiscountPercentage}% {currentLang === 'bn' ? 'ছাড়' : 'OFF'}</span>
                    <span className="savings-tag" style={{ background: 'rgba(34, 197, 94, 0.1)', color: '#16a34a', fontSize: '12px', padding: '4px 8px', borderRadius: '6px', fontWeight: 700 }}>
                      {currentLang === 'bn' ? `সাশ্রয়: ৳${websiteRegularPrice - websitePrice}` : `Save ৳${websiteRegularPrice - websitePrice}`}
                    </span>
                  </>
                ) : (
                  <span className="current-price" style={{ fontSize: '24px', fontWeight: 800, color: 'var(--primary, #7A0C0C)' }}>৳{websitePrice}</span>
                )}
              </div>

              <div className="rating-shipping-inline" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                {reviews.length > 0 && (
                  <div className="price-rating-display" style={{ margin: 0, padding: '4px 8px', display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'var(--bg-surface, #f9fafb)', border: '1px solid var(--border-color, #E5E7EB)', borderRadius: '6px' }}>
                    <StarRating
                      rating={reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length}
                      readOnly
                      size="small"
                    />
                    <span className="rating-count" style={{ fontSize: '12px', color: 'var(--text-secondary, #6b7280)', fontWeight: 600 }}>
                      ({(reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1)})
                    </span>
                  </div>
                )}

                {product.free_delivery && (
                  <span className="free-delivery-badge active" style={{ margin: 0, padding: '4px 8px', fontSize: '12px', borderRadius: '6px', display: 'inline-flex', alignItems: 'center', gap: '4px', border: '1px solid #86efac', background: '#dcfce7', color: '#16a34a', fontWeight: 600 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ stroke: '#16a34a' }}>
                      <rect x="1" y="3" width="15" height="13" />
                      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                      <circle cx="5.5" cy="18.5" r="2.5" />
                      <circle cx="18.5" cy="18.5" r="2.5" />
                    </svg>
                    <span>{currentLang === 'bn' ? 'ফ্রি ডেলিভারি' : 'Free Delivery'}</span>
                  </span>
                )}
              </div>
            </div>

            {/* Stock & Urgency Banner */}
            <div className="product-stock-container">
              {exactVariantAvailable ? (
                <div className="stock-info-row">
                  <span className="in-stock">
                    ✓ {t('product.inStock')}
                    {activeSku ? ` · Code: ${activeSku}` : ''}
                  </span>
                  {currentAvailableStock > 0 && currentAvailableStock <= 5 && (
                    <div className="low-stock-alert">
                      🔥 {currentLang === 'bn' ? `দ্রুত অর্ডার করুন! মাত্র ${currentAvailableStock}টি স্টকে আছে` : `Only ${currentAvailableStock} left in stock - order soon!`}
                    </div>
                  )}
                </div>
              ) : (
                <span className="out-of-stock">✗ {t('product.outOfStock')}{activeSku ? ` · Code: ${activeSku}` : ''}</span>
              )}

              {/* Sales & Views Indicators */}
              {((product.sales_count || 0) > 0 || (product.views_count || 0) > 0) && (
                <div className="product-stats-row">
                  {(product.sales_count || 0) > 0 && (
                    <span className="stat-pill">🛍️ {product.sales_count} {currentLang === 'bn' ? 'বিক্রি হয়েছে' : 'sold'}</span>
                  )}
                  {(product.views_count || 0) > 0 && (
                    <span className="stat-pill">👁️ {product.views_count} {currentLang === 'bn' ? 'জন দেখেছেন' : 'views'}</span>
                  )}
                </div>
              )}
            </div>

            {/* Variant Discount Coupon Chips */}
            {selectedVariant?.discount_codes && selectedVariant.discount_codes.length > 0 && (
              <div className="variant-coupons-box">
                <span className="coupon-title">🏷️ {currentLang === 'bn' ? 'উপলব্ধ কুপন:' : 'Available Coupon:'}</span>
                <div className="coupon-chips-list">
                  {selectedVariant.discount_codes.map((code) => (
                    <button
                      key={code}
                      type="button"
                      className="coupon-chip"
                      onClick={() => copyCouponCode(code)}
                      title="Click to copy coupon code"
                    >
                      <span>{code}</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Short Summary */}
            {hasSummaryContent && (
              <div className="product-summary">
                <h3>{currentLang === 'bn' ? 'সংক্ষিপ্ত বিবরণ' : 'Summary'}</h3>
                <div
                  className="summary-text"
                  dangerouslySetInnerHTML={{
                    __html: productSummary || ''
                  }}
                />
              </div>
            )}

{/* Attributes & Purchase Controls */}
            <div className="product-actions">
              {(
                colorOptions.length > 0 ||
                sizeOptions.length > 0 ||
                !!(product.attributes?.weights?.enabled && (product.attributes.weights.options || []).length > 0)
              ) && (
                <div className="product-attributes">
                  {/* Color selector */}
                  {colorOptions.length > 0 && (
                    <div className="attr-selector-group">
                      <span className="attr-selector-label">
                        {currentLang === 'bn' ? 'রং সিলেক্ট করুন' : 'Select Color'}
                        {selectedColor && <span className="attr-selected-value"> — {selectedColor}</span>}
                      </span>
                      <div className="attr-color-swatches">
                        {colorOptions.map(color => {
                          const hex = colorHexMap[color.toLowerCase()];
                          return (
                            <button
                              key={color}
                              type="button"
                              title={color}
                              className={`color-swatch-btn ${selectedColor === color ? 'selected' : ''}`}
                              onClick={() => { setSelectedColor(color); setSelectedSize(''); }}
                            >
                              <span
                                className="color-swatch-dot"
                                style={{ background: hex || color.toLowerCase() }}
                              ></span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Size selector */}
                  {sizeOptions.length > 0 && (
                    <div className="attr-selector-group">
                      <div className="attr-header-with-guide">
                        <span className="attr-selector-label">
                          {currentLang === 'bn' ? 'সাইজ সিলেক্ট করুন' : 'Select Size'}
                          {selectedSize && <span className="attr-selected-value"> — {selectedSize}</span>}
                        </span>
                        {product.size_chart_image && (
                          <button
                            type="button"
                            className="inline-size-chart-link"
                            onClick={() => setShowSizeGuide(true)}
                          >
                            📐 {currentLang === 'bn' ? 'সাইজ গাইড' : 'Size Guide'}
                          </button>
                        )}
                      </div>
                      <div className="attr-chips-row">
                        {sizeOptions.map(size => (
                          <button
                            key={size}
                            type="button"
                            className={`attr-chip-btn size-btn ${selectedSize === size ? 'selected' : ''}`}
                            onClick={() => setSelectedSize(prev => prev === size ? '' : size)}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Weight selector */}
                  {product.attributes?.weights?.enabled && (product.attributes.weights.options || []).length > 0 && (
                    <div className="attr-selector-group">
                      <span className="attr-selector-label">
                        {currentLang === 'bn' ? 'ওজন সিলেক্ট করুন' : 'Select Weight'}
                        {selectedWeight && <span className="attr-selected-value"> — {selectedWeight}</span>}
                      </span>
                      <div className="attr-chips-row">
                        {(product.attributes?.weights?.options || []).map(weight => (
                          <button
                            key={weight}
                            type="button"
                            className={`attr-chip-btn weight-btn ${selectedWeight === weight ? 'selected' : ''}`}
                            onClick={() => setSelectedWeight(prev => prev === weight ? '' : weight)}
                          >
                            {weight}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Quantity selector */}
              {/* Purchase Action Controls */}
              {exactVariantAvailable && (
                <div className="purchase-row" style={{ display: 'flex', gap: '12px', alignItems: 'center', width: '100%', marginBottom: '12px' }}>
                  <div className="quantity-controls" style={{ flexShrink: 0, height: '48px', padding: '3px 8px', display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid #E5E7EB', borderRadius: '14px', background: 'var(--bg-card, #ffffff)' }}>
                    <button type="button" onClick={() => handleQuantityChange(-1)} style={{ width: '28px', height: '28px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '18px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary, #333)' }} aria-label="Decrease quantity">−</button>
                    <span style={{ minWidth: '24px', textAlign: 'center', fontWeight: 700, fontSize: '15px', color: 'var(--text-main, #111)' }}>{quantity}</span>
                    <button type="button" onClick={() => handleQuantityChange(1)} style={{ width: '28px', height: '28px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '18px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary, #333)' }} aria-label="Increase quantity">+</button>
                  </div>

                  <button
                    className="add-to-cart-btn"
                    onClick={handleAddToCart}
                    disabled={!exactVariantAvailable}
                    style={{ flex: 1, height: '48px' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>
                    {t('buttons.addToCart')}
                  </button>
                </div>
              )}

              {/* Direct Purchase Button */}
              {exactVariantAvailable && (
                <button
                  className="direct-order-btn"
                  onClick={handleDirectOrder}
                  disabled={!exactVariantAvailable}
                  style={{ marginBottom: '12px' }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
                  {t('buttons.directOrder') || (currentLang === 'bn' ? 'অর্ডার করুন' : 'Order Now')}
                </button>
              )}

              {/* Contact Actions */}
              <div className="secondary-actions">
                <button
                  className="whatsapp-btn"
                  onClick={() => {
                    const whatsappNum = sessionStorage.getItem('whatsapp_number') || '8801741818807';
                    window.open(`https://wa.me/${whatsappNum}`, '_blank');
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.004 22.002c-2.733 0-5.301-.888-7.387-2.397l-3.66 1.15c-.297.094-.606-.057-.714-.346-.076-.201-.027-.428.122-.577l1.196-3.805A9.946 9.946 0 0 1 2.003 12c0-5.514 4.487-10 10-10s10 4.486 10 10-4.486 10.002-10 10.002zM6.07 18.062c1.724 1.298 3.82 1.996 6.02 2.001 5.016 0 9.098-4.083 9.098-9.098 0-5.016-4.082-9.098-9.098-9.098-5.016 0-9.098 4.082-9.098 9.098.001 2.186.772 4.29 2.14 5.992.12.15.138.356.046.525l-.64 2.036 1.942-.612a.455.455 0 0 1 .526.113l.064.043zm9.05-6.26c-.225-.112-1.332-.658-1.54-.733-.207-.075-.357-.113-.507.113-.15.225-.582.733-.713.882-.132.15-.263.169-.488.056-.225-.112-.95-.35-1.81-1.118-.67-.597-1.12-1.335-1.252-1.56-.131-.225-.014-.347.1-.459.1-.1.225-.262.338-.393.112-.131.15-.225.225-.375.075-.15.037-.28-.019-.393-.056-.112-.507-1.22-.695-1.67-.183-.438-.37-.378-.507-.385-.13-.006-.28-.006-.43-.006s-.394.056-.6.28c-.207.225-.79.77-.79 1.874s.807 2.174.92 2.324c.112.15 1.587 2.423 3.844 3.398.536.232.956.37 1.282.474.538.17 1.028.146 1.413.089.43-.064 1.332-.544 1.52-1.069.187-.525.187-.975.131-1.069-.056-.094-.207-.15-.432-.262z" />
                  </svg>
                  WhatsApp
                </button>
                <a
                  href={`tel:${sessionStorage.getItem('support_phone') || '+8801741818807'}`}
                  className="call-now-btn"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                  {t('buttons.callNow') || (currentLang === 'bn' ? 'কল করুন' : 'Call Now')}
                </a>
              </div>

              {/* Trust Features */}
              <div className="trust-badges">
                <div className="trust-badge">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                  <span>{currentLang === 'bn' ? 'নিরাপদ পেমেন্ট' : 'Secure Payment'}</span>
                </div>
                <div className="trust-badge">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>
                  <span>{currentLang === 'bn' ? 'দ্রুত ডেলিভারি' : 'Fast Delivery'}</span>
                </div>
                <div className="trust-badge">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2H3v16h5l4 4 4-4h5V2z"></path><path d="M12 11V7"></path><path d="M12 15h.01"></path></svg>
                  <span>{currentLang === 'bn' ? '২৪/৭ সাপোর্ট' : '24/7 Support'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabbed Product Details & Media Section */}
        <section className="product-tabs-section">
          <div className="product-tabs-header">
            <button
              className={`tab-btn ${activeTab === 'description' ? 'active' : ''}`}
              onClick={() => setActiveTab('description')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
              {currentLang === 'bn' ? 'পণ্যের বিবরণ' : 'Description'}
            </button>
            <button
              className={`tab-btn ${activeTab === 'shipping' ? 'active' : ''}`}
              onClick={() => setActiveTab('shipping')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>
              {currentLang === 'bn' ? 'ডেলিভারি ও রিটার্ন' : 'Shipping & Policy'}
            </button>
            <button
              className={`tab-btn ${activeTab === 'reviews' ? 'active' : ''}`}
              onClick={() => setActiveTab('reviews')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
              {currentLang === 'bn' ? `রিভিউ (${reviews.length})` : `Reviews (${reviews.length})`}
            </button>
          </div>

          <div className="product-tab-content">
            {/* Description Tab */}
            {activeTab === 'description' && (
              <div className="tab-pane fade-in">
                {!isHtmlEmpty(productDesc) ? (
                  <div
                    className="description-content"
                    dangerouslySetInnerHTML={{ __html: formatDescription(productDesc) }}
                  />
                ) : (
                  <p className="no-content-msg">
                    {currentLang === 'bn'
                      ? 'কোনো অতিরিক্ত বিস্তারিত বিবরণ পাওয়া যায়নি।'
                      : 'No additional detailed description available.'}
                  </p>
                )}

                {/* Extra media cards (Size chart only) */}
                {product.size_chart_image && (
                  <div className="product-extra-grid">
                    <div className="extra-card">
                      <h3>{currentLang === 'bn' ? 'সাইজ গাইড' : 'Size Guide'}</h3>
                      <button className="size-guide-preview" onClick={() => setShowSizeGuide(true)}>
                        <img src={getImageUrl(product.size_chart_image)} alt="Size guide" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Shipping Tab */}
            {activeTab === 'shipping' && (
              <div className="tab-pane fade-in shipping-info-pane">
                <div className="shipping-grid">
                  <div className="shipping-card">
                    <div className="shipping-icon">🚚</div>
                    <h4>{currentLang === 'bn' ? 'ঢাকার ভেতরে ডেলিভারি' : 'Delivery inside Dhaka'}</h4>
                    <p>{currentLang === 'bn' ? '২৪-৪৮ ঘণ্টার মধ্যে হোম ডেলিভারি।' : 'Home delivery within 24-48 hours.'}</p>
                    <span className="charge-tag">{currentLang === 'bn' ? 'চার্জ: ৳৭০' : 'Charge: ৳70'}</span>
                  </div>
                  <div className="shipping-card">
                    <div className="shipping-icon">📦</div>
                    <h4>{currentLang === 'bn' ? 'ঢাকার বাইরে ডেলিভারি' : 'Delivery outside Dhaka'}</h4>
                    <p>{currentLang === 'bn' ? '২-৪ দিনের মধ্যে ক্যাশ অন ডেলিভারি।' : 'Cash on delivery within 2-4 days.'}</p>
                    <span className="charge-tag">{currentLang === 'bn' ? 'চার্জ: ৳১৩০' : 'Charge: ৳130'}</span>
                  </div>
                  <div className="shipping-card">
                    <div className="shipping-icon">🔄</div>
                    <h4>{currentLang === 'bn' ? 'সহজ রিটার্ন পলিসি' : 'Easy 7 Days Return'}</h4>
                    <p>{currentLang === 'bn' ? 'পণ্য হাতে পেয়ে চেক করে নেওয়ার সুবিধা।' : 'Check product upon arrival with easy exchange.'}</p>
                    <span className="charge-tag">{currentLang === 'bn' ? '৭ দিনের রিটার্ন' : '7 Days Policy'}</span>
                  </div>
                </div>
              </div>
            )}

{/* Reviews Tab */}
            {activeTab === 'reviews' && (
              <div className="tab-pane fade-in reviews-pane">
                <div className="reviews-header-bar">
                  <h3>{t('reviews.customerReviews')}</h3>
                  {reviews.length > 0 && (
                    <div className="reviews-summary-badge">
                      <StarRating
                        rating={reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length}
                        readOnly
                        size="small"
                      />
                      <span className="reviews-count">
                        {(reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1)} / 5.0 ({reviews.length})
                      </span>
                    </div>
                  )}
                </div>

                <ReviewForm
                  productId={product.id}
                  onReviewSubmitted={() => loadReviews(product.id)}
                />

                <ReviewList reviews={reviews} loading={reviewsLoading} />
              </div>
            )}
          </div>
        </section>

        {/* Size Chart Modal */}
        {showSizeGuide && product.size_chart_image && (
          <div className="size-guide-modal" role="dialog" aria-modal="true" onClick={() => setShowSizeGuide(false)}>
            <div className="size-guide-dialog" onClick={event => event.stopPropagation()}>
              <button className="size-guide-close" onClick={() => setShowSizeGuide(false)}>×</button>
              <img src={getImageUrl(product.size_chart_image)} alt="Size guide" />
            </div>
          </div>
        )}

        {/* Related Products */}
        {product.related_products && product.related_products.length > 0 && (
          <section className="related-products">
            <h2>{t('product.relatedProducts')}</h2>
            <div className="products-grid">
              {product.related_products.flatMap(expandProductCards).map((item) => (
                <ProductCard key={`${item.product.id}-${item.color || 'default'}`} product={item.product} color={item.color} colorImage={item.colorImage} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Sticky Mobile Checkout Bar (< 768px) */}
      {exactVariantAvailable && (
        <div className="sticky-mobile-order-bar">
          <div className="mobile-bar-price">
            <span className="label">{currentLang === 'bn' ? 'মূল্য:' : 'Price:'}</span>
            <span className="amount">৳{websitePrice}</span>
          </div>
          <div className="mobile-bar-actions">
            <button className="mobile-cart-btn" onClick={handleAddToCart} title={t('buttons.addToCart')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>
            </button>
            <button className="mobile-direct-order-btn" onClick={handleDirectOrder}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><polyline points="17 11 19 13 23 9"></polyline></svg>
              <span>{t('buttons.directOrder') || (currentLang === 'bn' ? 'অর্ডার করুন' : 'Order Now')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetails;
